import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../util/errors';
import * as log from '../util/log';
import { createImageVariantConfig } from '../util/sdkLoader';
import { addMarketplaceEntities } from './marketplaceData.duck';

const NUM_CARDS = 4;

const fetchNewArrivalsPayloadCreator = (listingImageConfig, thunkAPI) => {
  const { extra: sdk, dispatch, rejectWithValue } = thunkAPI;
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    listingImageConfig || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      sort: 'createdAt',
      page: 1,
      perPage: NUM_CARDS,
      minStock: 1,
      stockMode: 'match-undefined',
      include: ['images', 'author'],
      'fields.listing': [
        'title',
        'price',
        'deleted',
        'state',
        'createdAt',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.retail_value',
        'publicData.purchase_price',
        'publicData.designer',
        'publicData.occasion',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        'variants.scaled-small',
        'variants.scaled-medium',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const listingIds = (response.data.data || []).map(l => l.id);
      return { listingIds };
    })
    .catch(e => {
      log.error(e, 'new-arrivals-fetch-failed');
      return rejectWithValue(storableError(e));
    });
};

export const fetchNewArrivals = createAsyncThunk(
  'newArrivals/fetchNewArrivals',
  fetchNewArrivalsPayloadCreator
);

const initialState = {
  listingIds: [],
  inProgress: false,
  fetched: false,
  error: null,
};

const newArrivalsSlice = createSlice({
  name: 'newArrivals',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchNewArrivals.pending, state => {
        state.inProgress = true;
        state.error = null;
      })
      .addCase(fetchNewArrivals.fulfilled, (state, action) => {
        state.listingIds = action.payload.listingIds;
        state.inProgress = false;
        state.fetched = true;
      })
      .addCase(fetchNewArrivals.rejected, (state, action) => {
        state.listingIds = [];
        state.inProgress = false;
        state.fetched = true;
        state.error = action.payload;
      });
  },
});

export default newArrivalsSlice.reducer;
